import React from "react";
import { TableRow, IconButton } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import Status from "../../../../shared/components/Status";
import { AccountingAccount } from "../../../../data/entities";
import ExpandIcon from "../../../../shared/svg/ExpandIcon";
import styled from "styled-components";
import ActionMenu from "./ActionMenu";
import { AccountCell, AccountCodeCell, ExpandCell } from "./Styled";

const ExpandButton = styled(IconButton)`
    width: 32px;
    height: 32px;
    padding: 0;
    min-width: 32px;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
`;

interface DisplayAccount extends AccountingAccount {
    level: number;
}

interface AccountingAccountRowProps {
    item: DisplayAccount;
    extraRowProps?: {
        expandedParents: Set<string>;
        toggleExpanded: (parentId: string) => void;
        allAccounts: AccountingAccount[];
        onRefresh?: () => void;
    };
}

const getStatusColorTheme = (status: string) => {
    switch (status) {
        case "ACTIVE":
            return "SUCCESS";
        case "INACTIVE":
            return "DISABLE";
        default:
            return "DISABLE";
    }
};

const getTypeText = (type: string) => {
    switch (type) {
        case "DU_NO":
            return "Dư nợ";
        case "DU_CO":
            return "Dư có";
        case "LUONG_TINH":
            return "Lưỡng tính";
        default:
            return type;
    }
};

const getStatusText = (status: string) => {
    switch (status) {
        case "ACTIVE":
            return "Hoạt động";
        case "INACTIVE":
            return "Không hoạt động";
        default:
            return status;
    }
};

export const AccountingAccountRow = ({
    item,
    extraRowProps,
}: AccountingAccountRowProps) => {
    const statusColorTheme = getStatusColorTheme(item.status);
    const typeText = getTypeText(item.type);
    const statusText = getStatusText(item.status);

    // Check if this account has any children (for any level)
    const hasChildren = extraRowProps?.allAccounts.some(
        (account) => account.parent === item.id
    );
    const isExpanded = extraRowProps?.expandedParents.has(item.id) || false;
    const isParent = Boolean(hasChildren);

    const handleToggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren && extraRowProps?.toggleExpanded) {
            extraRowProps.toggleExpanded(item.id);
        }
    };

    return (
        <TableRow>
            {/* Account Code Column with inline expand/collapse */}
            <AccountCodeCell align="left" level={item.level}>
                {hasChildren ? (
                    <ExpandButton onClick={handleToggleExpand} style={{ marginRight: 8 }}>
                        <ExpandIcon isExpanded={isExpanded} />
                    </ExpandButton>
                ) : (
                    <span style={{ display: "inline-block", width: 32, marginRight: 8 }} />
                )}
                <StyledNavLink to={`/accounting-accounts/${item.id}`}>
                    {item.code}
                </StyledNavLink>
            </AccountCodeCell>

            {/* Account Name Column - Highlighted for parent accounts */}
            <AccountCell align="left" isHighlighted={isParent && isExpanded}>
                {item.name}
            </AccountCell>

            {/* Other Columns */}
            <AccountCell align="left" isHighlighted={isParent && isExpanded}>
                {typeText}
            </AccountCell>
            <AccountCell align="left" isHighlighted={isParent && isExpanded}>
                {item.english_translation}
            </AccountCell>
            <AccountCell align="left" isHighlighted={isParent && isExpanded}>
                {item.note}
            </AccountCell>
            <AccountCell align="left" isHighlighted={isParent && isExpanded}>
                <Status
                    colorTheme={statusColorTheme}
                    text={statusText}
                    width="120px"
                />
            </AccountCell>

            {/* Action Menu Column */}
            <AccountCell align="center" isHighlighted={isParent && isExpanded}>
                <ActionMenu
                    account={item}
                    onRefresh={extraRowProps?.onRefresh}
                    isParent={isParent}
                />
            </AccountCell>
        </TableRow>
    );
};
